/**
 * Epic Pagos (V101) — Configuración → "Cobros en línea" (solo SUPER_USER).
 * El tenant conecta SU cuenta Stripe (Connect Standard, OAuth): el dinero de
 * las reservas prepagadas cae en SU cuenta; FluidSol solo facilita. Custodia
 * mínima: guardamos el acct_id, cero secretos. AUTO-GATEADA: si PAYMENTS_ENABLED
 * está apagado (kill-switch global) o el rol no es SUPER_USER, no se renderiza.
 */
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';

interface AccountStatus {
  enabled: boolean;
  connected: boolean;
  chargesEnabled: boolean;
  provider: string | null;
}

export function PaymentsConnectCard() {
  const { user } = useAuth() as any;
  const isSuper = user?.role === 'SUPER_USER';
  // Pagos (cobros en línea) está disponible SOLO en las verticales con POS físico
  // (GastroGest / Wellness / Boutique) — NO en Flow. El backend gatea global
  // (PAYMENTS_ENABLED), así que el filtro por vertical vive aquí, en el front.
  const mods: string[] = user?.tenant?.enabledModules?.length
    ? user.tenant.enabledModules
    : ['crm', 'boutique', 'gastro'];
  const isPos = mods.some((m) => m === 'gastro' || m === 'boutique' || m === 'gym');
  const [status, setStatus] = useState<AccountStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!isSuper || !isPos) return;
    api<AccountStatus>('/payments/account')
      .then(setStatus)
      .catch(() => { /* feature dormida o sin permiso: card no se muestra */ });
  }, [isSuper, isPos]);

  // Flow (sin POS) NO ve Cobros en línea; tampoco si el rol no es admin o el
  // kill-switch global está apagado.
  if (!isSuper || !isPos || !status?.enabled) return null;

  // V217 (§1ter): solo el TITULAR conecta (o reemplaza) la cuenta que recibe el dinero, con
  // confirmación explícita. Reemplazar NO tumba la cuenta actual: sigue cobrando hasta que
  // la nueva termine de conectarse en Stripe.
  const isOwner = !!user?.isOwner;
  const connect = async (replace = false) => {
    const ask = replace
      ? 'Vas a conectar una cuenta de Stripe NUEVA. Cuando termines en Stripe, los cobros en línea llegarán a esa cuenta y no a la actual. ¿Continuar?'
      : 'Vas a conectar la cuenta de Stripe que recibirá los cobros en línea de tu negocio. ¿Continuar?';
    if (!window.confirm(ask)) return;
    setBusy(true); setErr(null);
    try {
      const r = await api<{ url: string }>('/payments/connect/start', {
        method: 'POST', body: { confirm: true, replaceConnected: replace },
      });
      window.location.href = r.url;
    } catch (e: any) {
      setErr(e?.body?.message || 'No se pudo iniciar la conexión con Stripe.');
      setBusy(false);
    }
  };

  // Parte B — "Continuar verificación": link FRESCO cada clic (nunca uno guardado).
  // Reanuda el onboarding donde el tenant lo dejó; el refresh_url lo hace resistente
  // a ligas vencidas. Visible mientras la cuenta exista y no esté lista para cobrar.
  const continueVerification = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api<{ url: string }>('/payments/connect/refresh', { method: 'POST' });
      window.location.href = r.url;
    } catch (e: any) {
      setErr(e?.body?.message || 'No se pudo generar la liga de verificación.');
      setBusy(false);
    }
  };


  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
      <div className="border-b border-slate-100 px-6 py-5 md:px-8">
        <h3 className="font-serif text-lg tracking-tight text-slate-900">Cobros en línea</h3>
        <p className="text-sm text-slate-500">
          Cobra a tus clientes <strong>por adelantado</strong>. El dinero llega directo a
          <strong> tu cuenta de Stripe</strong>; FluidSol solo facilita el cobro.
        </p>
      </div>
      <div className="px-6 py-5 md:px-8">
        {status.connected ? (
          <div className="rounded-xl border border-slate-200 p-4">
            <div>
              <p className="text-sm font-medium text-slate-800">
                Cuenta Stripe conectada
                {status.chargesEnabled
                  ? <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">Cobros activos</span>
                  : <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">Verificación pendiente en Stripe</span>}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {status.chargesEnabled
                  ? 'Tu cuenta está lista para cobrar en línea a tus clientes.'
                  : 'Te falta completar la verificación con Stripe. Continúa donde te quedaste — puedes retomarla cuantas veces necesites.'}
              </p>
            </div>
            {/* Parte B — botón para RETOMAR la verificación (link fresco cada clic). */}
            {!status.chargesEnabled && (
              <div className="mt-3">
                <button onClick={continueVerification} disabled={busy}
                  className="rounded-xl bg-[#635BFF] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50">
                  {busy ? 'Redirigiendo…' : 'Continuar verificación'}
                </button>
              </div>
            )}
            {/* §4a — panel de Stripe del tenant. Connect STANDARD (4 cuentas live
                confirmadas por API): el tenant entra con SU cuenta al dashboard de Stripe;
                createLoginLink es Express-only, así que es enlace directo (sin backend). */}
            <div className="mt-3">
              <a href="https://dashboard.stripe.com/" target="_blank" rel="noopener noreferrer"
                className="inline-block rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                Ver mi panel de Stripe
              </a>
              <p className="mt-1 text-[11px] text-slate-400">Administra tus pagos, retiros y reembolsos en Stripe (inicias sesión con tu cuenta).</p>
            </div>
            {isOwner && (
              <div className="mt-3">
                <button onClick={() => connect(true)} disabled={busy}
                  className="text-xs font-medium text-slate-500 underline hover:text-slate-700 disabled:opacity-50">
                  Cambiar la cuenta que recibe los cobros
                </button>
              </div>
            )}
            {err && <p className="mt-2 text-sm text-rose-600">{err}</p>}
          </div>
        ) : (
          <div>
            <p className="mb-3 text-sm text-slate-600">
              Conecta tu cuenta de Stripe (o crea una) para empezar a cobrar en línea. Tú administras
              tus pagos, retiros y disputas desde tu propio panel de Stripe.
            </p>
            {/* Aviso de comisión de FluidSol — DEBE verse ANTES de completar la
                activación (rama no-conectada). El fee (PAYMENTS_FEE_PCT, 2% en prod) se calcula
                SOLO sobre bienes desde V137 (propina EXCLUIDA); el aviso debe casar con eso. La
                disclosure formal va con la nueva versión de TyC. */}
            <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3">
              <p className="text-sm text-amber-900">
                <strong>Comisión de FluidSol:</strong> 2% sobre el importe de productos y servicios, adicional a las comisiones que cobra Stripe. Las propinas no pagan comisión.
              </p>
            </div>
            {err && <p className="mb-2 text-sm text-rose-600">{err}</p>}
            {isOwner ? (
              <button onClick={() => connect(false)} disabled={busy}
                className="rounded-xl bg-[#635BFF] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50">
                {busy ? 'Redirigiendo…' : 'Conectar con Stripe'}
              </button>
            ) : (
              <p className="text-sm text-slate-500">
                Solo el titular de la cuenta{user?.ownerName ? ` (${user.ownerName})` : ''} puede conectar la cuenta que recibe los cobros.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
